/**
 * NÚMEROS — a lista e a ficha de cada número.
 *
 * A ficha existe para responder uma pergunta só: por que este número está (ou
 * não está) apto. Por isso o portão de aptidão vem primeiro e vem critério a
 * critério, com valor medido, limiar e o motivo em texto — um "não apto"
 * genérico não é auditável, e é justamente o que se cobra depois de uma queda.
 *
 * E por isso importar não libera: o Evolution só sabe se a sessão conectou;
 * quem decide se o número pode receber campanha é o plano de aquecimento.
 */
import { useEffect, useState } from "react";
import type { NumeroResumo, Plano } from "@co/contracts";
import { api, type NumeroFicha, type RespostaNumeros } from "../api.js";
import {
  Aviso,
  Botao,
  Campo,
  Carregando,
  Medidor,
  Painel as Cartao,
  Selo,
  Tabela,
  classeEntrada,
} from "../ui/base.js";
import { ErroAcao, ErroTela, useCarga } from "../ui/carga.js";
import {
  Definicoes,
  Filtros,
  LinhaVazia,
  Motivos,
  Paginacao,
  Rotulo,
  classeFiltro,
} from "../ui/dados.js";
import { Gaveta, Modal } from "../ui/gaveta.js";
import { conta, data, dataHora, dec, num, relativo } from "../ui/formato.js";
import { receber } from "../ui/alvo.js";
import {
  ESTADO_NUMERO,
  NIVEL_MIDIA,
  ORDEM_ESTADO,
  POOL,
} from "../ui/rotulos.js";

const LIMITE = 50;

type Sobreposicao =
  | { tipo: "nenhuma" }
  | { tipo: "ficha"; id: string }
  | { tipo: "cadastro" }
  | { tipo: "importacao" };

export function Numeros(): JSX.Element {
  // Chegada vinda do Painel: um número para abrir, ou um estado para filtrar.
  const [passagem] = useState(() => receber("numeros"));

  const [busca, setBusca] = useState("");
  const [buscaFirme, setBuscaFirme] = useState("");
  const [estado, setEstado] = useState(passagem?.estado ?? "");
  const [pool, setPool] = useState("");
  const [planoId, setPlanoId] = useState("");
  const [desloc, setDesloc] = useState(0);
  const [sobre, setSobre] = useState<Sobreposicao>(
    passagem?.numeroId ? { tipo: "ficha", id: passagem.numeroId } : { tipo: "nenhuma" },
  );

  // Digitar não pode disparar uma busca por tecla: a lista pisca e o servidor
  // recebe uma requisição por caractere.
  useEffect(() => {
    const t = setTimeout(() => {
      setBuscaFirme(busca.trim());
      setDesloc(0);
    }, 350);
    return () => clearTimeout(t);
  }, [busca]);

  const planos = usePlanos();

  const chave = `numeros|${buscaFirme}|${estado}|${pool}|${planoId}|${desloc}`;
  const { carga, recarregar } = useCarga<RespostaNumeros>(
    () =>
      api.numeros({
        busca: buscaFirme || undefined,
        estado: estado || undefined,
        pool: pool || undefined,
        planoId: planoId || undefined,
        limite: LIMITE,
        desloc,
      }),
    chave,
  );

  const dados = carga.fase === "pronto" ? carga.dados : null;
  const temFiltro = Boolean(buscaFirme || estado || pool || planoId);

  function limpar(): void {
    setBusca("");
    setBuscaFirme("");
    setEstado("");
    setPool("");
    setPlanoId("");
    setDesloc(0);
  }

  return (
    <div className="flex flex-col gap-5">
      <Cartao
        titulo="Números na incubadora"
        dica={
          dados
            ? `${num(dados.itens.length)} de ${num(dados.pagina.total)}`
            : "carregando…"
        }
        acoes={
          <>
            <Botao pequeno onClick={() => setSobre({ tipo: "importacao" })}>
              Importar do Evolution
            </Botao>
            <Botao
              pequeno
              tipo="principal"
              onClick={() => setSobre({ tipo: "cadastro" })}
            >
              Cadastrar número
            </Botao>
          </>
        }
        semCorpo
      >
        <div className="px-5 py-3 border-b border-linha">
          <Filtros>
            <input
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              placeholder="Buscar número, apelido, instância"
              aria-label="Buscar número"
              className={`${classeFiltro} min-w-[240px]`}
            />
            <select
              aria-label="Filtrar por estado"
              value={estado}
              onChange={(e) => {
                setEstado(e.target.value);
                setDesloc(0);
              }}
              className={classeFiltro}
            >
              <option value="">Todos os estados</option>
              {ORDEM_ESTADO.map((e) => (
                <option key={e} value={e}>
                  {ESTADO_NUMERO[e].rotulo}
                  {dados ? ` (${conta(dados.facetas.porEstado, e)})` : ""}
                </option>
              ))}
            </select>
            <select
              aria-label="Filtrar por pool"
              value={pool}
              onChange={(e) => {
                setPool(e.target.value);
                setDesloc(0);
              }}
              className={classeFiltro}
            >
              <option value="">Os dois pools</option>
              <option value="disparo">
                {POOL.disparo}
                {dados ? ` (${conta(dados.facetas.porPool, "disparo")})` : ""}
              </option>
              <option value="sdr">
                {POOL.sdr}
                {dados ? ` (${conta(dados.facetas.porPool, "sdr")})` : ""}
              </option>
            </select>
            <select
              aria-label="Filtrar por plano"
              value={planoId}
              onChange={(e) => {
                setPlanoId(e.target.value);
                setDesloc(0);
              }}
              className={classeFiltro}
            >
              <option value="">Todos os planos</option>
              {planos.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.nome}
                </option>
              ))}
            </select>
            {temFiltro && (
              <Botao pequeno tipo="fantasma" onClick={limpar}>
                Limpar filtros
              </Botao>
            )}
          </Filtros>
        </div>

        {carga.fase === "carregando" && <Carregando />}
        {carga.fase === "erro" && (
          <div className="p-5">
            <ErroTela mensagem={carga.mensagem} aoTentar={recarregar} />
          </div>
        )}
        {dados && (
          <>
            <TabelaNumeros
              itens={dados.itens}
              temFiltro={temFiltro}
              aoAbrir={(id) => setSobre({ tipo: "ficha", id })}
            />
            <Paginacao pagina={dados.pagina} aoIr={setDesloc} />
          </>
        )}
      </Cartao>

      {sobre.tipo === "ficha" && (
        <GavetaFicha
          id={sobre.id}
          aoFechar={() => setSobre({ tipo: "nenhuma" })}
          aoMudar={recarregar}
        />
      )}

      {sobre.tipo === "cadastro" && (
        <ModalCadastro
          planos={planos}
          aoFechar={() => setSobre({ tipo: "nenhuma" })}
          aoSalvar={() => {
            setSobre({ tipo: "nenhuma" });
            recarregar();
          }}
        />
      )}

      {sobre.tipo === "importacao" && (
        <ModalImportacao
          planos={planos}
          aoFechar={() => setSobre({ tipo: "nenhuma" })}
          aoImportar={() => {
            setSobre({ tipo: "nenhuma" });
            recarregar();
          }}
        />
      )}
    </div>
  );
}

/** Planos alimentam filtro e formulários; falha aqui não derruba a lista. */
function usePlanos(): Plano[] {
  const [planos, setPlanos] = useState<Plano[]>([]);
  useEffect(() => {
    let vivo = true;
    api
      .planos()
      .then((r) => {
        if (vivo) setPlanos(r.itens);
      })
      .catch(() => {
        if (vivo) setPlanos([]);
      });
    return () => {
      vivo = false;
    };
  }, []);
  return planos;
}

/* ── Tabela ───────────────────────────────────────────────────────── */

function TabelaNumeros({
  itens,
  temFiltro,
  aoAbrir,
}: {
  itens: NumeroResumo[];
  temFiltro: boolean;
  aoAbrir: (id: string) => void;
}): JSX.Element {
  return (
    <Tabela
      larguraMin={1000}
      colunas={[
        { chave: "n", rotulo: "Número" },
        { chave: "e", rotulo: "Estado" },
        { chave: "p", rotulo: "Plano e etapa" },
        { chave: "g", rotulo: "Progresso" },
        { chave: "l", rotulo: "Liberação" },
        { chave: "u", rotulo: "Uso hoje", direita: true },
        { chave: "a", rotulo: "Última atividade" },
      ]}
    >
      {itens.length === 0 ? (
        <LinhaVazia colunas={7}>
          {temFiltro
            ? "Nenhum número corresponde aos filtros."
            : "Nenhum número cadastrado. Importe do Evolution ou cadastre um a um."}
        </LinhaVazia>
      ) : (
        itens.map((n) => {
          const info = ESTADO_NUMERO[n.estado];
          return (
            <tr
              key={n.id}
              className="border-t border-linha hover:bg-superficie-2"
            >
              <td className="px-5 py-3 align-top">
                <button
                  type="button"
                  onClick={() => aoAbrir(n.id)}
                  className="text-left"
                  title="Abrir a ficha"
                >
                  <span className="block text-[13px] font-medium tabular">
                    {n.telefoneExibicao}
                  </span>
                  <span className="block text-[11.5px] text-tinta-3">
                    {n.apelido ?? "sem apelido"}
                    {n.instancia ? ` · ${n.instancia}` : ""}
                  </span>
                </button>
              </td>
              <td className="px-5 py-3 align-top">
                <Selo tom={info.tom} titulo={info.nota}>
                  {info.rotulo}
                </Selo>
                <span className="block text-[11px] text-tinta-3 mt-1">
                  {POOL[n.pool]} · {NIVEL_MIDIA[n.nivelMidia]}
                </span>
              </td>
              <td className="px-5 py-3 align-top text-[12.5px]">
                {n.planoNome ?? "sem plano"}
                <span className="block text-[11.5px] text-tinta-3">
                  {n.etapaNome ?? "sem etapa em curso"}
                </span>
              </td>
              <td className="px-5 py-3 align-top">
                <Medidor pct={n.progressoPct} />
              </td>
              <td className="px-5 py-3 align-top">
                {n.liberado ? (
                  <Selo tom="ok">Liberado</Selo>
                ) : (
                  <Selo tom="idle" titulo="Conectado não é apto">
                    Sem liberação
                  </Selo>
                )}
              </td>
              <td className="px-5 py-3 align-top text-right">
                <span className="text-[13px] tabular">
                  {num(n.hoje.enviadas)} / {num(n.hoje.teto)}
                </span>
                <span className="block text-[11px] text-tinta-3 tabular">
                  folga {num(n.hoje.folga)} · recebidas {num(n.hoje.recebidas)}
                </span>
              </td>
              <td className="px-5 py-3 align-top">
                <span className="text-[12px] tabular text-tinta-2">
                  {dataHora(n.ultimaAtividadeEm, "nunca")}
                </span>
                <span className="block text-[11px] text-tinta-3">
                  {relativo(n.ultimaAtividadeEm)}
                </span>
              </td>
            </tr>
          );
        })
      )}
    </Tabela>
  );
}

/* ── Ficha ────────────────────────────────────────────────────────── */

type PedidoMotivo = { acao: "liberar" | "revogar" } | null;

function GavetaFicha({
  id,
  aoFechar,
  aoMudar,
}: {
  id: string;
  aoFechar: () => void;
  aoMudar: () => void;
}): JSX.Element {
  const { carga, recarregar } = useCarga<NumeroFicha>(
    () => api.numeroFicha(id),
    `ficha|${id}`,
  );
  const [pedido, setPedido] = useState<PedidoMotivo>(null);
  const [erroAcao, setErroAcao] = useState<string | null>(null);

  const ficha = carga.fase === "pronto" ? carga.dados : null;
  const n = ficha?.numero;
  const info = n ? ESTADO_NUMERO[n.estado] : null;

  async function executar(acao: "liberar" | "revogar", motivo: string): Promise<void> {
    setErroAcao(null);
    try {
      if (acao === "liberar") await api.liberar(id, motivo || undefined);
      else await api.revogar(id, motivo);
      setPedido(null);
      recarregar();
      aoMudar();
    } catch (e) {
      setErroAcao(e instanceof Error ? e.message : "Não foi possível concluir.");
    }
  }

  return (
    <>
      <Gaveta
        rotulo={`Ficha do número ${n?.telefoneExibicao ?? ""}`}
        aoFechar={aoFechar}
        cabecalho={
          <>
            <div className="text-[17px] font-semibold tracking-tight tabular truncate">
              {n?.telefoneExibicao ?? "Carregando…"}
            </div>
            <div className="text-[12.5px] text-tinta-3 truncate">
              {n
                ? `${n.apelido ?? "sem apelido"} · ${n.instancia ?? "sem instância"}`
                : ""}
            </div>
            {n && info && (
              <div className="mt-2 flex gap-1.5 flex-wrap">
                <Selo tom={info.tom}>{info.rotulo}</Selo>
                <Selo tom="cool">{POOL[n.pool]}</Selo>
                <Selo tom="idle">{NIVEL_MIDIA[n.nivelMidia]}</Selo>
                {n.liberado ? (
                  <Selo tom="ok">Liberado</Selo>
                ) : (
                  <Selo tom="idle">Sem liberação</Selo>
                )}
              </div>
            )}
          </>
        }
        rodape={
          <div className="flex items-center gap-2 flex-wrap">
            <Botao
              pequeno
              tipo="principal"
              desabilitado={!ficha}
              onClick={() => setPedido({ acao: "liberar" })}
            >
              Liberar para campanha
            </Botao>
            <Botao
              pequeno
              tipo="perigo"
              desabilitado={!ficha?.liberacao}
              titulo={
                ficha?.liberacao
                  ? "Revogar exige motivo — ele vai para o histórico"
                  : "Não há liberação vigente para revogar"
              }
              onClick={() => setPedido({ acao: "revogar" })}
            >
              Revogar liberação
            </Botao>
            <span className="text-[11px] text-tinta-3 ml-auto">
              Toda ação manual entra no histórico com autor, data e motivo.
            </span>
          </div>
        }
      >
        {carga.fase === "carregando" && <Carregando />}
        {carga.fase === "erro" && (
          <ErroTela mensagem={carga.mensagem} aoTentar={recarregar} />
        )}

        {erroAcao && <ErroAcao>{erroAcao}</ErroAcao>}

        {ficha && (
          <>
            <BlocoAptidao ficha={ficha} />
            <BlocoLiberacao ficha={ficha} />
            <BlocoTempos ficha={ficha} />
            <BlocoLedger ficha={ficha} />
            <BlocoSessoes ficha={ficha} />
          </>
        )}
      </Gaveta>

      {pedido && (
        <ModalMotivo
          titulo={
            pedido.acao === "liberar"
              ? "Liberar o número para campanha"
              : "Revogar a liberação"
          }
          dica={
            pedido.acao === "liberar"
              ? "Liberar contra o portão é uma decisão sua, e fica registrada como tal."
              : "O motivo é obrigatório: número revogado sem causa registrada some da série de mortalidade."
          }
          obrigatorio={pedido.acao === "revogar"}
          rotuloConfirmar={pedido.acao === "liberar" ? "Liberar" : "Revogar"}
          perigo={pedido.acao === "revogar"}
          aoFechar={() => setPedido(null)}
          aoConfirmar={(motivo) => void executar(pedido.acao, motivo)}
        />
      )}
    </>
  );
}

function BlocoAptidao({ ficha }: { ficha: NumeroFicha }): JSX.Element {
  const passaram = ficha.aptidao.filter((c) => c.passou).length;
  const total = ficha.aptidao.length;
  const reprovados = ficha.aptidao.filter((c) => !c.passou);

  return (
    <section>
      <Rotulo>
        Portão de aptidão — {passaram} de {total} critérios cumpridos
      </Rotulo>
      <p className="text-[12px] text-tinta-3 mt-1 mb-3 leading-snug">
        O portão é conjuntivo: um critério reprovado reprova o número inteiro.
        Não há média nem compensação entre critérios.
      </p>

      {total === 0 ? (
        <Aviso tom="idle">
          Nenhum critério avaliado ainda. O portão só roda depois da primeira
          atividade do plano.
        </Aviso>
      ) : (
        <>
          {reprovados.length === 0 ? (
            <Aviso tom="ok">
              Todos os critérios passaram. Se ainda não está liberado, falta a
              aprovação prevista no plano.
            </Aviso>
          ) : (
            <Aviso tom="warn">
              {reprovados.length === 1
                ? "Um critério reprovado segura o número."
                : `${reprovados.length} critérios reprovados seguram o número.`}
            </Aviso>
          )}

          <div className="mt-3 border border-linha rounded-[10px] overflow-hidden">
            <Tabela
              larguraMin={560}
              colunas={[
                { chave: "c", rotulo: "Critério" },
                { chave: "m", rotulo: "Medido", direita: true },
                { chave: "l", rotulo: "Limiar", direita: true },
                { chave: "v", rotulo: "Veredito" },
              ]}
            >
              {ficha.aptidao.map((c) => (
                <tr key={c.criterio} className="border-t border-linha">
                  <td className="px-5 py-2.5 align-top text-[12.5px]">
                    {c.criterio}
                    <span className="block text-[11.5px] text-tinta-3 leading-snug mt-0.5">
                      {c.motivo}
                    </span>
                  </td>
                  <td className="px-5 py-2.5 align-top text-right text-[12.5px] tabular">
                    {c.valorMedido}
                  </td>
                  <td className="px-5 py-2.5 align-top text-right text-[12.5px] tabular text-tinta-3">
                    {c.limiar}
                  </td>
                  <td className="px-5 py-2.5 align-top">
                    {c.passou ? (
                      <Selo tom="ok">Passou</Selo>
                    ) : (
                      <Selo tom="crit">Não passou</Selo>
                    )}
                  </td>
                </tr>
              ))}
            </Tabela>
          </div>
        </>
      )}
    </section>
  );
}

function BlocoLiberacao({ ficha }: { ficha: NumeroFicha }): JSX.Element {
  const l = ficha.liberacao;

  if (!l) {
    return (
      <section>
        <Rotulo>Liberação</Rotulo>
        <div className="mt-2">
          <Aviso tom="idle">
            Sem liberação vigente. Sessão conectada não é número apto — só a
            liberação autoriza campanha a usar este número.
          </Aviso>
        </div>
      </section>
    );
  }

  return (
    <section>
      <Rotulo>Liberação vigente</Rotulo>
      <div className="mt-2">
        <Definicoes
          itens={[
            { k: "Liberado em", v: dataHora(l.liberadoEm) },
            {
              k: "Aprovado por",
              v: `${l.aprovadoPor ?? "motor"} (${
                l.origem === "manual" ? "decisão manual" : "decisão automática"
              })`,
            },
            { k: "Capacidade inicial", v: `${num(l.capacidadeInicial)} contatos/dia` },
            { k: "Reavaliação", v: data(l.proximaReavaliacao) },
          ]}
        />
      </div>
      {l.motivos.length > 0 && (
        <div className="mt-3">
          <Motivos
            itens={l.motivos.map((m) => ({ texto: m, sinal: "positivo" as const }))}
          />
        </div>
      )}
    </section>
  );
}

function BlocoTempos({ ficha }: { ficha: NumeroFicha }): JSX.Element {
  const t = ficha.tempos;
  return (
    <section>
      <Rotulo>Os três tempos</Rotulo>
      <p className="text-[12px] text-tinta-3 mt-1 mb-3 leading-snug">
        Idade, disponibilidade e continuidade medem coisas diferentes. Um número
        antigo que cai toda hora não é um número maduro — e é a continuidade que
        denuncia isso.
      </p>
      <Definicoes
        itens={[
          {
            k: "Idade",
            v:
              t.idadeDias === null
                ? "sem data de origem registrada"
                : `${num(t.idadeDias)} dia(s) desde o cadastro`,
          },
          {
            k: "Uptime 30d",
            v:
              t.uptime30d === null
                ? "sem histórico suficiente"
                : `${dec(t.uptime30d)}% do período com sessão ativa`,
          },
          {
            k: "Continuidade",
            v:
              t.continuidadeHoras === null
                ? "sessão nunca esteve estável"
                : `${dec(t.continuidadeHoras)} h desde a última queda`,
          },
          {
            k: "Quedas em 7d",
            v:
              t.flaps7d === 0
                ? "nenhuma queda na semana"
                : `${num(t.flaps7d)} queda(s) na semana`,
          },
        ]}
      />
    </section>
  );
}

function BlocoLedger({ ficha }: { ficha: NumeroFicha }): JSX.Element {
  return (
    <section>
      <Rotulo>Ledger dos últimos 30 dias</Rotulo>
      <p className="text-[12px] text-tinta-3 mt-1 mb-3 leading-snug">
        O teto de cada dia vem com o motivo do cálculo. Teto sem motivo é número
        mágico: ninguém consegue contestar nem corrigir.
      </p>
      <div className="border border-linha rounded-[10px] overflow-hidden">
        <Tabela
          larguraMin={720}
          colunas={[
            { chave: "d", rotulo: "Dia" },
            { chave: "e", rotulo: "Env.", direita: true },
            { chave: "r", rotulo: "Rec.", direita: true },
            { chave: "x", rotulo: "Externas", direita: true },
            { chave: "i", rotulo: "Distintos", direita: true },
            { chave: "b", rotulo: "Bloq.", direita: true },
            { chave: "n", rotulo: "Den.", direita: true },
            { chave: "t", rotulo: "Teto" },
          ]}
        >
          {ficha.ledger.length === 0 ? (
            <LinhaVazia colunas={8}>
              Nenhum dia registrado ainda para este número.
            </LinhaVazia>
          ) : (
            ficha.ledger.map((d) => (
              <tr key={d.dia} className="border-t border-linha align-top">
                <td className="px-5 py-2 text-[12px] tabular whitespace-nowrap">
                  {data(d.dia)}
                </td>
                <td className="px-5 py-2 text-right text-[12px] tabular">
                  {num(d.enviadas)}
                </td>
                <td className="px-5 py-2 text-right text-[12px] tabular">
                  {num(d.recebidas)}
                </td>
                <td className="px-5 py-2 text-right text-[12px] tabular">
                  {num(d.recebidasExternas)}
                </td>
                <td className="px-5 py-2 text-right text-[12px] tabular">
                  {num(d.distintos)}
                </td>
                <td className="px-5 py-2 text-right text-[12px] tabular">
                  {d.bloqueios > 0 ? (
                    <Selo tom="warn">{num(d.bloqueios)}</Selo>
                  ) : (
                    num(d.bloqueios)
                  )}
                </td>
                <td className="px-5 py-2 text-right text-[12px] tabular">
                  {d.denuncias > 0 ? (
                    <Selo tom="crit">{num(d.denuncias)}</Selo>
                  ) : (
                    num(d.denuncias)
                  )}
                </td>
                <td className="px-5 py-2 text-[12px] min-w-[200px]">
                  <span className="tabular">{num(d.tetoCalculado)}</span>
                  {d.confianca !== null && (
                    <span className="text-tinta-3">
                      {" "}
                      · confiança {dec(d.confianca)}
                    </span>
                  )}
                  {d.motivoTeto.length > 0 && (
                    <span className="block text-[11px] text-tinta-3 leading-snug mt-0.5">
                      {d.motivoTeto.join(" · ")}
                    </span>
                  )}
                </td>
              </tr>
            ))
          )}
        </Tabela>
      </div>
    </section>
  );
}

function BlocoSessoes({ ficha }: { ficha: NumeroFicha }): JSX.Element {
  return (
    <section>
      <Rotulo>Histórico de sessão</Rotulo>
      <p className="text-[12px] text-tinta-3 mt-1 mb-3 leading-snug">
        A causa é reportada como veio, nunca rateada: somar conflito de sessão
        com ban envenena a única régua de mortalidade que existe.
      </p>
      {ficha.sessoes.length === 0 ? (
        <div className="text-[13px] text-tinta-3 py-4">
          Nenhum evento de sessão registrado.
        </div>
      ) : (
        <ul className="list-none m-0 p-0">
          {ficha.sessoes.map((s, i) => (
            <li
              key={`${s.ocorridoEm}-${i}`}
              className="grid grid-cols-[96px_1fr] gap-3 py-2 border-b border-linha last:border-b-0 text-[12.5px]"
            >
              <span className="text-[11px] text-tinta-3 tabular">
                {dataHora(s.ocorridoEm)}
              </span>
              <span className="min-w-0">
                {s.tipo}
                <span className="block text-[11.5px] text-tinta-3 leading-snug">
                  {s.causa ? `causa: ${s.causa}` : "causa não determinada"}
                  {s.detalhe ? ` — ${s.detalhe}` : ""}
                </span>
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/* ── Modal de motivo ──────────────────────────────────────────────── */

function ModalMotivo({
  titulo,
  dica,
  obrigatorio,
  rotuloConfirmar,
  perigo,
  aoFechar,
  aoConfirmar,
}: {
  titulo: string;
  dica: string;
  obrigatorio: boolean;
  rotuloConfirmar: string;
  perigo: boolean;
  aoFechar: () => void;
  aoConfirmar: (motivo: string) => void;
}): JSX.Element {
  const [motivo, setMotivo] = useState("");
  const vazio = obrigatorio && motivo.trim() === "";

  return (
    <Modal
      titulo={titulo}
      dica={dica}
      largura={520}
      aoFechar={aoFechar}
      rodape={
        <>
          <Botao tipo="fantasma" onClick={aoFechar}>
            Cancelar
          </Botao>
          <Botao
            tipo={perigo ? "perigo" : "principal"}
            desabilitado={vazio}
            onClick={() => aoConfirmar(motivo.trim())}
          >
            {rotuloConfirmar}
          </Botao>
        </>
      }
    >
      <Campo
        rotulo={obrigatorio ? "Motivo (obrigatório)" : "Motivo (opcional)"}
        dica="Fica registrado no histórico e na ficha do número."
      >
        <textarea
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
          rows={4}
          className={classeEntrada}
        />
      </Campo>
    </Modal>
  );
}

/* ── Cadastro ─────────────────────────────────────────────────────── */

function ModalCadastro({
  planos,
  aoFechar,
  aoSalvar,
}: {
  planos: Plano[];
  aoFechar: () => void;
  aoSalvar: () => void;
}): JSX.Element {
  const [telefone, setTelefone] = useState("");
  const [apelido, setApelido] = useState("");
  const [instancia, setInstancia] = useState("");
  const [pool, setPool] = useState("disparo");
  const [planoId, setPlanoId] = useState(planos[0]?.id ?? "");
  const [operadora, setOperadora] = useState("");
  const [grupoInfra, setGrupoInfra] = useState("");
  const [tipoLinha, setTipoLinha] = useState("");
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);

  const podeSalvar = telefone.trim().length >= 8 && planoId !== "";

  async function salvar(): Promise<void> {
    setErro(null);
    setSalvando(true);
    try {
      // Campo vazio não é campo ausente: mandar "" gravaria operadora vazia
      // como se fosse informação declarada.
      const dados: Record<string, string> = { telefone: telefone.trim(), pool, planoId };
      if (apelido.trim()) dados["apelido"] = apelido.trim();
      if (instancia.trim()) dados["instancia"] = instancia.trim();
      if (operadora.trim()) dados["operadora"] = operadora.trim();
      if (grupoInfra.trim()) dados["grupoInfra"] = grupoInfra.trim();
      if (tipoLinha) dados["tipoLinha"] = tipoLinha;
      await api.criarNumero(dados);
      aoSalvar();
    } catch (e) {
      setErro(e instanceof Error ? e.message : "Não foi possível cadastrar.");
    } finally {
      setSalvando(false);
    }
  }

  return (
    <Modal
      titulo="Cadastrar número na incubadora"
      dica="O número entra no plano escolhido e começa do zero. Cadastrar não libera."
      aoFechar={aoFechar}
      rodape={
        <>
          <Botao tipo="fantasma" onClick={aoFechar}>
            Cancelar
          </Botao>
          <Botao
            tipo="principal"
            desabilitado={!podeSalvar || salvando}
            onClick={() => void salvar()}
          >
            {salvando ? "Cadastrando…" : "Cadastrar"}
          </Botao>
        </>
      }
    >
      {erro && <ErroAcao>{erro}</ErroAcao>}

      {planos.length === 0 && (
        <Aviso tom="warn">
          Nenhum plano de aquecimento disponível. Crie um plano antes de
          cadastrar números — número sem plano não avança.
        </Aviso>
      )}

      <div className="grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(200px,1fr))]">
        <Campo rotulo="Número com país e DDD" dica="Ex.: +55 11 90000-0000">
          <input
            value={telefone}
            onChange={(e) => setTelefone(e.target.value)}
            className={classeEntrada}
            placeholder="+55 11 90000-0000"
          />
        </Campo>
        <Campo rotulo="Apelido interno">
          <input
            value={apelido}
            onChange={(e) => setApelido(e.target.value)}
            className={classeEntrada}
            placeholder="Comercial 04"
          />
        </Campo>
        <Campo rotulo="Instância no Evolution" dica="Uma instância é um número, para sempre.">
          <input
            value={instancia}
            onChange={(e) => setInstancia(e.target.value)}
            className={classeEntrada}
            placeholder="sessao-04"
          />
        </Campo>
        <Campo rotulo="Pool">
          <select
            value={pool}
            onChange={(e) => setPool(e.target.value)}
            className={classeEntrada}
          >
            <option value="disparo">{POOL.disparo}</option>
            <option value="sdr">{POOL.sdr}</option>
          </select>
        </Campo>
        <Campo rotulo="Plano de aquecimento">
          <select
            value={planoId}
            onChange={(e) => setPlanoId(e.target.value)}
            className={classeEntrada}
          >
            <option value="">Escolha um plano</option>
            {planos.map((p) => (
              <option key={p.id} value={p.id}>
                {p.nome}
              </option>
            ))}
          </select>
        </Campo>
        <Campo rotulo="Operadora">
          <input
            value={operadora}
            onChange={(e) => setOperadora(e.target.value)}
            className={classeEntrada}
          />
        </Campo>
        <Campo rotulo="Grupo de infraestrutura" dica="Serve para detectar queda correlacionada.">
          <input
            value={grupoInfra}
            onChange={(e) => setGrupoInfra(e.target.value)}
            className={classeEntrada}
          />
        </Campo>
        <Campo rotulo="Tipo de linha">
          <select
            value={tipoLinha}
            onChange={(e) => setTipoLinha(e.target.value)}
            className={classeEntrada}
          >
            <option value="">Não informado</option>
            <option value="prepago">Pré-pago</option>
            <option value="pos_cnpj">Pós-pago em CNPJ</option>
          </select>
        </Campo>
      </div>
    </Modal>
  );
}

/* ── Importação do Evolution: ler → conferir → importar ───────────── */

interface ItemPrevia {
  instancia: string;
  telefone: string;
  telefoneExibicao: string;
  perfil: string;
  conectada: boolean;
  jaCadastrado: boolean;
}

const texto = (v: unknown): string => (typeof v === "string" ? v : "");
const booleano = (v: unknown): boolean => v === true;

/**
 * O cliente devolve a prévia como registro solto; o contrato já descreve a
 * forma. Normalizar aqui evita que campo faltando vire `undefined` renderizado
 * na tela de conferência — que é justamente onde o operador confia nos olhos.
 */
function normalizarPrevia(bruto: Array<Record<string, unknown>>): ItemPrevia[] {
  return bruto.map((i) => ({
    instancia: texto(i["instancia"]),
    telefone: texto(i["telefone"]),
    telefoneExibicao: texto(i["telefoneExibicao"]) || texto(i["telefone"]),
    perfil: texto(i["perfil"]),
    conectada: booleano(i["conectada"]),
    jaCadastrado: booleano(i["jaCadastrado"]),
  }));
}

function ModalImportacao({
  planos,
  aoFechar,
  aoImportar,
}: {
  planos: Plano[];
  aoFechar: () => void;
  aoImportar: () => void;
}): JSX.Element {
  const [itens, setItens] = useState<ItemPrevia[] | null>(null);
  const [escolhidas, setEscolhidas] = useState<string[]>([]);
  const [lendo, setLendo] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [planoId, setPlanoId] = useState(planos[0]?.id ?? "");
  const [pool, setPool] = useState("disparo");
  const [confirmaOrigem, setConfirmaOrigem] = useState(false);
  const [importando, setImportando] = useState(false);

  async function ler(): Promise<void> {
    setErro(null);
    setLendo(true);
    try {
      const r = await api.previaImportacao();
      const lidos = normalizarPrevia(r.itens);
      setItens(lidos);
      setEscolhidas(lidos.filter((i) => !i.jaCadastrado).map((i) => i.instancia));
    } catch (e) {
      setErro(e instanceof Error ? e.message : "Não foi possível ler o Evolution.");
    } finally {
      setLendo(false);
    }
  }

  async function importar(): Promise<void> {
    setErro(null);
    setImportando(true);
    try {
      await api.importar({
        instancias: escolhidas,
        planoId,
        pool,
        confirmaOrigem: true,
      });
      aoImportar();
    } catch (e) {
      setErro(e instanceof Error ? e.message : "Não foi possível importar.");
    } finally {
      setImportando(false);
    }
  }

  const novos = itens?.filter((i) => !i.jaCadastrado).length ?? 0;
  const podeImportar =
    escolhidas.length > 0 && planoId !== "" && confirmaOrigem && !importando;

  return (
    <Modal
      titulo="Importar números do Evolution"
      dica="Três passos: ler as instâncias, conferir uma a uma e só então importar."
      largura={760}
      aoFechar={aoFechar}
      rodape={
        <>
          <Botao tipo="fantasma" onClick={aoFechar}>
            Cancelar
          </Botao>
          <Botao
            tipo="principal"
            desabilitado={!podeImportar}
            onClick={() => void importar()}
          >
            {importando
              ? "Importando…"
              : `Importar ${escolhidas.length} selecionado(s)`}
          </Botao>
        </>
      }
    >
      {erro && <ErroAcao>{erro}</ErroAcao>}

      <Aviso tom="warn">
        <strong>Importar não libera.</strong> O número entra como cadastrado ou
        conectado e ainda precisa cumprir o plano de aquecimento antes de
        receber campanha. Conectado não é apto — e isso vale igual para número
        que já vem em uso.
      </Aviso>

      <section>
        <Rotulo>1. Ler as instâncias do Evolution</Rotulo>
        <div className="mt-2 flex items-center gap-3 flex-wrap">
          <Botao pequeno onClick={() => void ler()} desabilitado={lendo}>
            {lendo ? "Lendo…" : itens ? "Ler de novo" : "Ler instâncias"}
          </Botao>
          {itens && (
            <span className="text-[12px] text-tinta-3">
              {itens.length} instância(s) lida(s), {novos} nova(s).
            </span>
          )}
        </div>
      </section>

      {itens && (
        <section>
          <Rotulo>2. Conferir antes de importar</Rotulo>
          <div className="mt-2 border border-linha rounded-[10px] max-h-[280px] overflow-auto">
            <Tabela
              larguraMin={620}
              colunas={[
                { chave: "s", rotulo: "Importar" },
                { chave: "n", rotulo: "Número" },
                { chave: "i", rotulo: "Instância" },
                { chave: "p", rotulo: "Perfil" },
                { chave: "c", rotulo: "Sessão" },
              ]}
            >
              {itens.length === 0 ? (
                <LinhaVazia colunas={5}>
                  O Evolution não devolveu nenhuma instância.
                </LinhaVazia>
              ) : (
                itens.map((i) => {
                  const marcada = escolhidas.includes(i.instancia);
                  return (
                    <tr key={i.instancia} className="border-t border-linha">
                      <td className="px-5 py-2">
                        <input
                          type="checkbox"
                          checked={marcada}
                          disabled={i.jaCadastrado}
                          aria-label={`Importar ${i.telefoneExibicao || i.instancia}`}
                          onChange={(e) =>
                            setEscolhidas((atual) =>
                              e.target.checked
                                ? [...atual, i.instancia]
                                : atual.filter((x) => x !== i.instancia),
                            )
                          }
                        />
                      </td>
                      <td className="px-5 py-2 text-[12.5px] tabular">
                        {i.telefoneExibicao || "—"}
                      </td>
                      <td className="px-5 py-2 text-[12px] text-tinta-2">
                        {i.instancia || "—"}
                      </td>
                      <td className="px-5 py-2 text-[12.5px]">
                        {i.perfil || "—"}
                      </td>
                      <td className="px-5 py-2">
                        {i.jaCadastrado ? (
                          <Selo tom="idle">Já cadastrado</Selo>
                        ) : i.conectada ? (
                          <Selo tom="ok">Conectada</Selo>
                        ) : (
                          <Selo tom="warn">Sem sessão</Selo>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </Tabela>
          </div>
        </section>
      )}

      <section>
        <Rotulo>3. Como entram na plataforma</Rotulo>
        <div className="mt-2 grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(200px,1fr))]">
          <Campo rotulo="Plano de aquecimento">
            <select
              value={planoId}
              onChange={(e) => setPlanoId(e.target.value)}
              className={classeEntrada}
            >
              <option value="">Escolha um plano</option>
              {planos.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.nome}
                </option>
              ))}
            </select>
          </Campo>
          <Campo rotulo="Pool">
            <select
              value={pool}
              onChange={(e) => setPool(e.target.value)}
              className={classeEntrada}
            >
              <option value="disparo">{POOL.disparo}</option>
              <option value="sdr">{POOL.sdr}</option>
            </select>
          </Campo>
        </div>

        <label className="flex items-start gap-2 mt-3 text-[12.5px] text-tinta-2 leading-snug">
          <input
            type="checkbox"
            checked={confirmaOrigem}
            onChange={(e) => setConfirmaOrigem(e.target.checked)}
            className="mt-0.5"
          />
          <span>
            Confirmo que estes números são próprios e controlados, e que as
            interações respeitam quem pediu para não ser contactado.
          </span>
        </label>
      </section>
    </Modal>
  );
}
